import * as React from 'react'
import { Boxes, Plus, ChevronRight } from 'lucide-react'
import { useVendorFurniture, useVendorFurnitureGroups, useCreateVendorFurnitureGroup, useUpdateVendorFurnitureGroup } from '@/hooks/useVendorFurniture'
import { requestErrorMessage } from '@/helpers/apiClient'
import { Button } from '@/components/ui/button'
import { LoadingState, ErrorState, EmptyState } from '@/components/ui/states'
import { Input } from '@/components/ui/input'
import { Field } from '@/components/ui/field'
import { FormError } from '@/components/ui/form-error'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { GroupEditor } from '@/components/lazyDrawings'
import { groupSeating } from '@/lib/groupSeating'
import type { FurnitureGroupDto } from '@lib/dto/furniture-group.dto'
import type { FurnitureGroupMemberInput } from '@lib/input/furniture-group.input'
/** A vendor's reusable furniture groups: a flat library (no venue scoping) whose
 * members are arranged from the vendor's own catalog items. */
export function VendorFurnitureGroupsPanel() {
  const groupsQuery = useVendorFurnitureGroups()
  const furniture = useVendorFurniture()
  const createGroup = useCreateVendorFurnitureGroup()
  const updateGroup = useUpdateVendorFurnitureGroup()

  const [open, setOpen] = React.useState(false)
  const [editingId, setEditingId] = React.useState<string | null>(null)
  const [name, setName] = React.useState('')
  const [members, setMembers] = React.useState<FurnitureGroupMemberInput[]>([])
  const [submitError, setSubmitError] = React.useState<string | null>(null)

  const catalog = furniture.data?.items ?? []
  // The same tally the arranger shows, so the button and the message under it
  // cannot disagree about whether the arrangement fits.
  const { over: overCapacity } = groupSeating(catalog, members)
  const busy = createGroup.isPending || updateGroup.isPending

  function openCreate(): void {
    setEditingId(null)
    setName('')
    setMembers([])
    setSubmitError(null)
    setOpen(true)
  }

  function openEdit(group: FurnitureGroupDto): void {
    setEditingId(group.id)
    setName(group.name)
    setMembers(group.members.map((m) => ({ furnitureItemId: m.furnitureItemId, x: m.x, y: m.y, rotation: m.rotation })))
    setSubmitError(null)
    setOpen(true)
  }

  async function save(): Promise<void> {
    setSubmitError(null)
    try {
      if (editingId) {
        await updateGroup.mutateAsync({ id: editingId, input: { name, members } })
      } else {
        await createGroup.mutateAsync({ name, members })
      }
      setOpen(false)
    } catch (error) {
      setSubmitError(requestErrorMessage(error))
    }
  }

  function renderList() {
    if (groupsQuery.isPending) {
      return (
        <LoadingState label="Loading groups…" />
      )
    }
    if (groupsQuery.isError) {
      return (
        <ErrorState message={requestErrorMessage(groupsQuery.error)} onRetry={() => void groupsQuery.refetch()} />
      )
    }
    if (groupsQuery.data.groups.length === 0) {
      return (
        <EmptyState
          icon={Boxes}
          title="No groups yet"
          description="Arrange your catalog items into a reusable group — a table with its chairs, say."
          action={
            <Button onClick={openCreate}>
              <Plus className="h-4 w-4" /> New group
            </Button>
          }
        />
      )
    }
    return (
      <ul className="divide-y divide-line overflow-hidden rounded-md border border-line">
        {groupsQuery.data.groups.map((group) => (
          <li key={group.id}>
            <button
              type="button"
              onClick={() => openEdit(group)}
              className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-hover"
            >
              <Boxes className="h-5 w-5 shrink-0 text-ink-muted" />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">{group.name}</span>
                <span className="text-xs text-ink-muted">
                  {group.members.length} {group.members.length === 1 ? 'piece' : 'pieces'}
                </span>
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-ink-muted" />
            </button>
          </li>
        ))}
      </ul>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4" /> New group
        </Button>
      </div>
      {renderList()}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit group' : 'New group'}</DialogTitle>
            <DialogDescription>Name it and arrange its pieces from your catalog.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <Field label="Name" htmlFor="vfg-name">
              <Input id="vfg-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Reception round" autoFocus />
            </Field>
            <Field label="Pieces">
              <GroupEditor items={catalog} members={members} onChange={setMembers} />
            </Field>
            {submitError ? <FormError>{submitError}</FormError> : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            {/* An over-seated arrangement is refused by the API, so the button
                that would send it is shut rather than left to fail. The reason is
                already on screen, under the arranger. */}
            <Button pending={busy} type="button" onClick={save} disabled={name.trim() === '' || members.length === 0 || overCapacity !== null}>
              {busy ? 'Saving…' : 'Save group'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
